import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { fetchPrices } from '@/features/billing/billingThunks';
import {
  selectCheckoutPromoCode,
  selectPrices,
  selectSubscriptionState,
} from '@/features/billing/billingSlice';
import { fetchUserWorkspaces, fetchWorkspaceTeam } from '@/features/workspace/workspaceThunks';
import {
  switchWorkspace,
  isWorkspaceLegacyTrialExpired,
} from '@/features/workspace/workspaceSlice';
import type { Workspace } from '@/features/workspace/types';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import PaymentSection from './PaymentSection';
import PaymentValidation from './PaymentValidation';
import PlanSection from './PlanSection';
import {
  isEntitledSubscriptionStatus,
  isPartnerAccountOffer,
  isPaymentOverdueStatus,
} from './usageExpiredUtils';

const PLAN_EXPIRED = 'Expired';

function isWelyaEmail(email: string | null | undefined): boolean {
  return Boolean(email?.toLowerCase().endsWith('@welya.io'));
}

type Step = 1 | 2 | 3;

type UsageExpiredAlertProps = {
  isConfig?: boolean;
  triggerClassName?: string;
  triggerLabel?: string;
  onSubscribed?: () => void;
  /** Ouvre la modale au montage, sans bouton déclencheur. Reste fermable. */
  autoOpen?: boolean;
  onClose?: () => void;
};

export default function UsageExpiredAlert({
  isConfig = false,
  triggerClassName,
  triggerLabel,
  onSubscribed,
  autoOpen = false,
  onClose,
}: UsageExpiredAlertProps) {
  const { t } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const usage = useAppSelector((s: RootState) => s.billing.usage.data);
  const subscriptionState = useAppSelector(selectSubscriptionState);
  const prices = useAppSelector(selectPrices);
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const user = useAppSelector((s: RootState) => s.auth.user);
  const currentWorkspace = useAppSelector((s: RootState) => s.workspace.currentWorkspace);
  const checkoutPromoCode = useAppSelector(selectCheckoutPromoCode);
  const team = useAppSelector((s: RootState) => s.workspace.team);
  const teamLoading = useAppSelector((s: RootState) => s.workspace.teamLoading);
  const workspacesList = useAppSelector((s: RootState) => s.workspace.workspacesList);
  const workspacesLoading = useAppSelector((s: RootState) => s.workspace.loading);
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null);
  const [isYearly, setIsYearly] = useState(true);
  const [successDismissed, setSuccessDismissed] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(autoOpen);

  const isWelyaUser = isWelyaEmail(user?.email);
  const workspaceOwner = team.find((member) => member.creator);
  const isWorkspaceOwnedByWelya = isWelyaEmail(workspaceOwner?.email);
  const isBillingAlertSuppressed = !isWelyaUser && (teamLoading || isWorkspaceOwnedByWelya);
  const isPastDue = isPaymentOverdueStatus(subscriptionState.data?.status);

  // Seul un abonnement réel devenu inactif (résiliation, expiration) bloque. On exige un
  // objet d'abonnement avec un statut : une réponse vide (= pas d'abonnement) ne bloque pas.
  // Un compte gratuit pur / legacy encore valide n'a pas de statut → ne bloque pas.
  const isSubscriptionBlocked =
    !isPastDue &&
    !isWelyaUser &&
    !subscriptionState.loading &&
    Boolean(subscriptionState.data?.status) &&
    !isEntitledSubscriptionStatus(subscriptionState.data?.status);

  // Ancien compte trial dont le sursis legacy est expiré : le back bascule en 402 partout
  // (« cessation de paiement ») → on lui affiche le parcours de paiement.
  const isLegacyTrialExpired = !isWelyaUser && isWorkspaceLegacyTrialExpired(currentWorkspace);

  const isExpired =
    !isPastDue &&
    (usage?.planName === PLAN_EXPIRED || isSubscriptionBlocked || isLegacyTrialExpired);

  // Compte créé par le partenaire : le back restreint les tarifs et applique déjà sa remise.
  const isPartnerOffer = isPartnerAccountOffer(prices);

  const otherWorkspaces = (workspacesList ?? []).filter((w) => w.id !== workspaceId);

  useEffect(() => {
    if (workspaceId) {
      void dispatch(fetchWorkspaceTeam({ workspaceId, page: 0, size: 30 }));
    }
  }, [workspaceId, dispatch]);

  // Charge la liste des workspaces dès que la dialog est bloquante,
  // pour proposer le switcher si l'utilisateur en a d'autres.
  useEffect(() => {
    if (isExpired && workspacesList == null && !workspacesLoading) {
      void dispatch(fetchUserWorkspaces({ page: 0 }));
    }
  }, [isExpired, workspacesList, workspacesLoading, dispatch]);

  useEffect(() => {
    if (step !== 3) {
      setStep(1);
      setSelectedPlanKey(null);
      setIsYearly(true);
      setSuccessDismissed(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId && !isBillingAlertSuppressed && (isExpired || (isConfig && configModalOpen))) {
      dispatch(
        fetchPrices({
          workspaceId,
          ...(checkoutPromoCode && { promoCode: checkoutPromoCode }),
        }),
      );
    }
  }, [
    workspaceId,
    isExpired,
    isConfig,
    configModalOpen,
    isBillingAlertSuppressed,
    checkoutPromoCode,
    dispatch,
  ]);

  const handleSwitchWorkspace = (workspace: Workspace) => {
    dispatch(
      switchWorkspace({
        ...workspace,
        socials: workspace.socials ?? [],
        visualIdentity: workspace.visualIdentity ?? null,
      }),
    );
    navigate('/');
  };

  const open =
    !successDismissed &&
    !isBillingAlertSuppressed &&
    (isExpired || step === 3 || (isConfig && configModalOpen));
  const canClose = isConfig && !isExpired;

  const handleOpenChange = (next: boolean) => {
    if (next) {
      if (isConfig) setConfigModalOpen(true);
      return;
    }
    if (isConfig && canClose) {
      setConfigModalOpen(false);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {isConfig && !autoOpen && (
        <DialogTrigger asChild>
          <Button className={triggerClassName}>
            {triggerLabel ?? t('usageExpired.choosePlan')}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent
        className={step === 3 ? 'max-w-[580px]' : 'max-w-[1350px] max-h-[90vh] overflow-y-auto'}
        hideCloseButton={isExpired}
        onInteractOutside={isExpired ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={isExpired ? (e) => e.preventDefault() : undefined}
      >
        {step === 1 && (
          <PlanSection
            prices={prices}
            isTrial={false}
            isExpired={isExpired}
            onSelectPlan={(planKey) => {
              setSelectedPlanKey(planKey);
              setStep(2);
            }}
            isWelyaUser={isWelyaUser}
            isPartnerOffer={isPartnerOffer}
            isYearly={isYearly}
            onIsYearlyChange={setIsYearly}
          />
        )}
        {step === 2 && (
          <PaymentSection
            prices={prices}
            selectedPlanKey={selectedPlanKey}
            isYearly={isYearly}
            workspaceId={workspaceId}
            workspace={currentWorkspace}
            user={user}
            onBack={() => setStep(1)}
            onSuccess={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <PaymentValidation
            workspaceName={currentWorkspace?.name}
            selectedPlanKey={selectedPlanKey}
            user={user}
            confirmLabel={onSubscribed ? t('paymentValidation.backToLive') : undefined}
            onBackToDashboard={() => {
              setSuccessDismissed(true);
              if (onSubscribed) onSubscribed();
              else navigate('/');
            }}
          />
        )}

        {isExpired && otherWorkspaces.length > 0 && (
          <div className="mt-4">
            <Separator className="mb-6" />
            <p className="text-sm font-medium text-secondary mb-3">
              {t('usageExpired.otherWorkspaceTitle')}
            </p>
            <div className="space-y-2">
              {workspacesLoading ? (
                <p className="text-sm text-tertiary">{t('usageExpired.loading')}</p>
              ) : (
                otherWorkspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-medium p-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="size-8 shrink-0 rounded-full bg-gradient-brand" aria-hidden />
                      <p className="font-medium truncate">{workspace.name}</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSwitchWorkspace(workspace)}
                    >
                      {t('usageExpired.switchButton')}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
